import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap";
import axios from "axios";
import swal from "sweetalert";

import Widget from "../../components/Widget/Widget";
import { API_TIMEOUT, SUMBER, SUB_SUMBER } from "../../utils";

const initialFetchState = {
  loading: false,
  error: null,
};

const TableSumberSubsumber = () => {
  const [sumberState, setSumberState] = useState(initialFetchState);
  const [subsumberState, setSubsumberState] = useState(initialFetchState);

  const [sumberList, setSumberList] = useState([]);
  const [subsumberList, setSubsumberList] = useState([]);

  const [filterSumberId, setFilterSumberId] = useState("all");

  const [sumberModalOpen, setSumberModalOpen] = useState(false);
  const [subsumberModalOpen, setSubsumberModalOpen] = useState(false);

  const [sumberFormValue, setSumberFormValue] = useState("");
  const [subsumberFormValue, setSubsumberFormValue] = useState("");
  const [subsumberSumberId, setSubsumberSumberId] = useState("");

  const [editingSumber, setEditingSumber] = useState(null);
  const [editingSubsumber, setEditingSubsumber] = useState(null);

  const [saving, setSaving] = useState(false);

  const sumberMap = useMemo(() => {
    const map = new Map();
    sumberList.forEach((item) => {
      map.set(Number(item.id), item.sumber);
    });
    return map;
  }, [sumberList]);

  const filteredSubsumber = useMemo(() => {
    if (filterSumberId === "all") {
      return subsumberList;
    }
    const idNumber = Number(filterSumberId);
    return subsumberList.filter((item) => Number(item.sumber) === idNumber);
  }, [subsumberList, filterSumberId]);

  const handleRequestError = useCallback((message, error) => {
    const responseMessage =
      error?.response?.data?.message || message || "Terjadi kesalahan";
    swal("Error", responseMessage, "error");
  }, []);

  const fetchSumber = useCallback(async () => {
    setSumberState({ loading: true, error: null });
    try {
      const response = await axios.get(SUMBER, {
        timeout: API_TIMEOUT,
      });
      setSumberList(response.data?.data || []);
      setSumberState({ loading: false, error: null });
    } catch (error) {
      setSumberState({ loading: false, error });
      handleRequestError("Gagal memuat data sumber", error);
    }
  }, [handleRequestError]);

  const fetchSubsumber = useCallback(async () => {
    setSubsumberState({ loading: true, error: null });
    try {
      const response = await axios.get(SUB_SUMBER, {
        timeout: API_TIMEOUT,
      });
      setSubsumberList(response.data?.data || []);
      setSubsumberState({ loading: false, error: null });
    } catch (error) {
      setSubsumberState({ loading: false, error });
      handleRequestError("Gagal memuat data sub sumber", error);
    }
  }, [handleRequestError]);

  useEffect(() => {
    fetchSumber();
    fetchSubsumber();
  }, [fetchSumber, fetchSubsumber]);

  const resetSumberForm = () => {
    setSumberFormValue("");
    setEditingSumber(null);
  };

  const resetSubsumberForm = () => {
    setSubsumberFormValue("");
    setSubsumberSumberId("");
    setEditingSubsumber(null);
  };

  const toggleSumberModal = () => {
    setSumberModalOpen((prev) => !prev);
    if (sumberModalOpen) {
      resetSumberForm();
    }
  };

  const toggleSubsumberModal = () => {
    setSubsumberModalOpen((prev) => !prev);
    if (subsumberModalOpen) {
      resetSubsumberForm();
    }
  };

  const handleAddSumber = () => {
    resetSumberForm();
    setSumberModalOpen(true);
  };

  const handleEditSumber = (item) => {
    setEditingSumber(item);
    setSumberFormValue(item.sumber || "");
    setSumberModalOpen(true);
  };

  const handleDeleteSumber = (item) => {
    swal({
      title: "Hapus sumber?",
      text: `Sumber "${item.sumber}" akan dihapus permanen.`,
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      try {
        await axios.delete(`${SUMBER}${item.id}`, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data sumber berhasil dihapus", "success");
        fetchSumber();
        fetchSubsumber();
      } catch (error) {
        handleRequestError("Gagal menghapus sumber", error);
      }
    });
  };

  const handleSaveSumber = async (event) => {
    event.preventDefault();
    if (!sumberFormValue.trim()) {
      swal("Validasi", "Nama sumber wajib diisi", "warning");
      return;
    }

    const payload = { sumber: sumberFormValue.trim() };
    setSaving(true);
    try {
      if (editingSumber) {
        await axios.patch(`${SUMBER}${editingSumber.id}`, payload, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data sumber berhasil diperbarui", "success");
      } else {
        await axios.post(SUMBER, payload, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data sumber berhasil ditambahkan", "success");
      }
      setSaving(false);
      setSumberModalOpen(false);
      resetSumberForm();
      fetchSumber();
      if (!editingSumber) {
        fetchSubsumber();
      }
    } catch (error) {
      setSaving(false);
      handleRequestError("Gagal menyimpan sumber", error);
    }
  };

  const handleAddSubsumber = () => {
    resetSubsumberForm();
    setSubsumberModalOpen(true);
  };

  const handleEditSubsumber = (item) => {
    setEditingSubsumber(item);
    setSubsumberFormValue(item.subsumber || "");
    setSubsumberSumberId(String(item.sumber));
    setSubsumberModalOpen(true);
  };

  const handleDeleteSubsumber = (item) => {
    swal({
      title: "Hapus sub sumber?",
      text: `Sub sumber "${item.subsumber}" akan dihapus permanen.`,
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      try {
        await axios.delete(`${SUB_SUMBER}${item.id}`, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data sub sumber berhasil dihapus", "success");
        fetchSubsumber();
      } catch (error) {
        handleRequestError("Gagal menghapus sub sumber", error);
      }
    });
  };

  const handleSaveSubsumber = async (event) => {
    event.preventDefault();
    if (!subsumberFormValue.trim()) {
      swal("Validasi", "Nama sub sumber wajib diisi", "warning");
      return;
    }
    if (!subsumberSumberId) {
      swal("Validasi", "Pilih sumber terlebih dahulu", "warning");
      return;
    }

    const payload = {
      subsumber: subsumberFormValue.trim(),
      idSumber: Number(subsumberSumberId),
    };

    setSaving(true);
    try {
      if (editingSubsumber) {
        await axios.patch(`${SUB_SUMBER}${editingSubsumber.id}`, payload, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data sub sumber berhasil diperbarui", "success");
      } else {
        await axios.post(SUB_SUMBER, payload, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data sub sumber berhasil ditambahkan", "success");
      }
      setSaving(false);
      setSubsumberModalOpen(false);
      resetSubsumberForm();
      fetchSubsumber();
    } catch (error) {
      setSaving(false);
      handleRequestError("Gagal menyimpan sub sumber", error);
    }
  };

  const renderLoadingRow = (colSpan) => (
    <tr>
      <td colSpan={colSpan} className="text-center py-4">
        <Spinner size="sm" color="primary" className="mr-2" />
        Memuat data...
      </td>
    </tr>
  );

  const renderEmptyRow = (message, colSpan) => (
    <tr>
      <td colSpan={colSpan} className="text-center py-4 text-muted">
        {message}
      </td>
    </tr>
  );

  return (
    <div className="sumber-subsumber-page">
      <Row className="gy-4">
        <Col lg={6} className="mb-4">
          <Widget
            title="Data Sumber"
            containerClass="shadow-sm border-0"
            className="p-0"
          >
            <Card className="border-0">
              <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center px-3 py-3">
                <span className="mb-0 font-weight-semibold">
                  Daftar Sumber Obat
                </span>
                <Button color="primary" size="sm" onClick={handleAddSumber}>
                  Tambah Sumber
                </Button>
              </CardHeader>
              <CardBody className="px-3 pb-3">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "10%" }}>No</th>
                        <th>Nama Sumber</th>
                        <th style={{ width: "20%" }} className="text-center">
                          Aksi
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sumberState.loading
                        ? renderLoadingRow(3)
                        : sumberList.length === 0
                        ? renderEmptyRow("Belum ada data sumber", 3)
                        : sumberList.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.sumber}</td>
                              <td className="text-center">
                                <Button
                                  size="sm"
                                  color="info"
                                  className="mr-2"
                                  onClick={() => handleEditSumber(item)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => handleDeleteSumber(item)}
                                >
                                  Hapus
                                </Button>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Widget>
        </Col>

        <Col lg={6} className="mb-4">
          <Widget
            title="Data Sub Sumber"
            containerClass="shadow-sm border-0"
            className="p-0"
          >
            <Card className="border-0">
              <CardHeader className="bg-transparent border-0 px-3 py-3">
                <div className="d-flex flex-column flex-lg-row align-items-lg-center">
                  <div className="flex-grow-1 mb-3 mb-lg-0">
                    <strong>Daftar Sub Sumber</strong>
                  </div>
                  <div className="d-flex align-items-center">
                    <Input
                      type="select"
                      value={filterSumberId}
                      onChange={(event) =>
                        setFilterSumberId(event.target.value)
                      }
                      className="mr-2"
                      style={{ minWidth: 180 }}
                    >
                      <option value="all">Semua Sumber</option>
                      {sumberList.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.sumber}
                        </option>
                      ))}
                    </Input>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleAddSubsumber}
                      disabled={sumberList.length === 0}
                    >
                      Tambah Sub Sumber
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="px-3 pb-3">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "10%" }}>No</th>
                        <th>Nama Sub Sumber</th>
                        <th>Sumber</th>
                        <th style={{ width: "20%" }} className="text-center">
                          Aksi
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subsumberState.loading
                        ? renderLoadingRow(4)
                        : filteredSubsumber.length === 0
                        ? renderEmptyRow("Belum ada data sub sumber", 4)
                        : filteredSubsumber.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.subsumber}</td>
                              <td>
                                {sumberMap.get(Number(item.sumber)) || "-"}
                              </td>
                              <td className="text-center">
                                <Button
                                  size="sm"
                                  color="info"
                                  className="mr-2"
                                  onClick={() => handleEditSubsumber(item)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => handleDeleteSubsumber(item)}
                                >
                                  Hapus
                                </Button>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Widget>
        </Col>
      </Row>

      <Modal isOpen={sumberModalOpen} toggle={toggleSumberModal}>
        <Form onSubmit={handleSaveSumber}>
          <ModalHeader toggle={toggleSumberModal}>
            {editingSumber ? "Edit Sumber" : "Tambah Sumber"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="nama-sumber">Nama Sumber</Label>
              <Input
                id="nama-sumber"
                type="text"
                value={sumberFormValue}
                onChange={(event) => setSumberFormValue(event.target.value)}
                placeholder="Masukkan nama sumber"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleSumberModal}>
              Batal
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" color="light" className="mr-2" />
                  Menyimpan...
                </>
              ) : editingSumber ? (
                "Simpan Perubahan"
              ) : (
                "Tambah"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={subsumberModalOpen} toggle={toggleSubsumberModal}>
        <Form onSubmit={handleSaveSubsumber}>
          <ModalHeader toggle={toggleSubsumberModal}>
            {editingSubsumber ? "Edit Sub Sumber" : "Tambah Sub Sumber"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="pilih-sumber">Sumber</Label>
              <Input
                id="pilih-sumber"
                type="select"
                value={subsumberSumberId}
                onChange={(event) => setSubsumberSumberId(event.target.value)}
              >
                <option value="">-- Pilih Sumber --</option>
                {sumberList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.sumber}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="nama-subsumber">Nama Sub Sumber</Label>
              <Input
                id="nama-subsumber"
                type="text"
                value={subsumberFormValue}
                onChange={(event) => setSubsumberFormValue(event.target.value)}
                placeholder="Masukkan nama sub sumber"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleSubsumberModal}>
              Batal
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" color="light" className="mr-2" />
                  Menyimpan...
                </>
              ) : editingSubsumber ? (
                "Simpan Perubahan"
              ) : (
                "Tambah"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default TableSumberSubsumber;
